import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Key, Plus, Trash2, Copy, Check, AlertCircle, Loader2, RefreshCw, ShieldOff, ShieldCheck, Clock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatFR = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' });
};

const ApiKeysPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [generated, setGenerated] = useState(null); // { plain_key, item }
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/api-keys`, { headers, timeout: 12000 });
      setItems(data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/api-keys`,
        { name: newName.trim() },
        { headers, timeout: 12000 },
      );
      setGenerated(data);
      setCreateOpen(false);
      setNewName('');
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de création');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      await axios.patch(`${API}/admin/api-keys/${id}`, { status: 'revoked' }, { headers });
      setConfirmRevoke(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la révocation');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/api-keys/${id}`, { headers });
      setConfirmDelete(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {/* ignore */}
  };

  const activeCount = items.filter((i) => i.status === 'active').length;
  const revokedCount = items.filter((i) => i.status === 'revoked').length;
  const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/integrations`;

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="api-keys-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Key className="w-6 h-6 text-[#52a675]" /> Clés API
          </h1>
          <p className="text-sm text-gray-500">
            Permettent à des plateformes externes (CRM, ERP…) de récupérer la liste des clients en lecture seule.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="btn-keys-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Actualiser
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#52a675] hover:bg-[#3d8a5c]" size="sm" data-testid="btn-keys-create">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle clé
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border border-gray-200 p-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Total</div>
          <div className="text-xl font-bold">{items.length}</div>
        </div>
        <div className="rounded-lg border border-green-200 bg-green-50/40 p-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Actives</div>
          <div className="text-xl font-bold text-green-700">{activeCount}</div>
        </div>
        <div className="rounded-lg border border-gray-200 p-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Révoquées</div>
          <div className="text-xl font-bold text-gray-500">{revokedCount}</div>
        </div>
      </div>

      {/* Doc d'intégration */}
      <Card className="bg-gradient-to-br from-[#e8f5ef]/60 to-white border-[#52a675]/30">
        <CardContent className="p-5 space-y-3">
          <h2 className="text-sm font-semibold text-gray-900">Comment utiliser une clé API</h2>
          <div className="text-xs text-gray-700 space-y-2">
            <p>Ajoute le header <code className="bg-white px-1.5 py-0.5 rounded border">X-API-Key: ck_live_…</code> sur tes requêtes.</p>
            <div className="bg-gray-900 text-gray-100 rounded p-3 font-mono text-[11px] overflow-x-auto">
              {`# Health-check de la clé\n`}
              {`curl -H "X-API-Key: ck_live_…" ${baseUrl}/ping\n\n`}
              {`# Liste paginée des clients (max 500/page, sync incrémentale via updated_since)\n`}
              {`curl -H "X-API-Key: ck_live_…" "${baseUrl}/customers?limit=100&updated_since=2026-01-01T00:00:00Z"`}
            </div>
            <p className="text-gray-600">
              Format unifié par client : <code>customer</code>, <code>vehicle</code>, <code>subscription</code> (plan, franchise, durée, prix), <code>seller_code</code>. Pas d'IDs Stripe ni détails de paiement.
            </p>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Liste des clés */}
      {loading ? (
        <div className="py-10 text-center text-gray-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Chargement…</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-gray-500">
            <Key className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            Aucune clé API. Crée la première pour connecter ton CRM externe.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((k) => (
            <div
              key={k.id}
              className="rounded-xl border border-gray-200 bg-white p-4 flex flex-wrap items-start gap-3"
              data-testid={`api-key-row-${k.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-semibold text-gray-900">{k.name}</span>
                  {k.status === 'active' ? (
                    <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-[11px]">
                      <ShieldCheck className="w-3 h-3 mr-1" /> Active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-200 text-[11px]">
                      <ShieldOff className="w-3 h-3 mr-1" /> Révoquée
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-gray-500 font-mono">{k.key_prefix}</div>
                <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                  <span><Clock className="w-3 h-3 inline mr-0.5" /> Créée le {formatFR(k.created_at)} par {k.created_by}</span>
                  <span>Dernière utilisation : {formatFR(k.last_used_at)}</span>
                  <span>{k.usage_count || 0} requêtes</span>
                  {k.revoked_at && <span className="text-red-600">Révoquée le {formatFR(k.revoked_at)}</span>}
                </div>
              </div>
              <div className="flex gap-2">
                {k.status === 'active' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={() => setConfirmRevoke(k)}
                    data-testid={`btn-revoke-${k.id}`}
                  >
                    <ShieldOff className="w-3 h-3 mr-1" /> Révoquer
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => setConfirmDelete(k)}
                  data-testid={`btn-delete-${k.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle clé API</DialogTitle>
            <DialogDescription>
              Donne un nom à cette clé pour identifier l'intégration. La valeur secrète ne sera affichée qu'une seule fois.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label htmlFor="key-name">Nom de l'intégration *</Label>
              <Input
                id="key-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ex : CRM-Atelier"
                maxLength={80}
                required
                data-testid="input-key-name"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#52a675] hover:bg-[#3d8a5c]" disabled={creating || !newName.trim()} data-testid="btn-confirm-create-key">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-1" />}
                Générer la clé
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Generated dialog — show plain key ONCE */}
      <Dialog open={!!generated} onOpenChange={(o) => !o && setGenerated(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#52a675]" /> Clé API créée</DialogTitle>
            <DialogDescription className="text-red-600 font-semibold">
              ⚠️ Cette valeur n'est affichée qu'une seule fois. Copie-la maintenant — tu ne pourras plus la retrouver.
            </DialogDescription>
          </DialogHeader>
          {generated && (
            <div className="space-y-3">
              <div className="bg-gray-900 text-green-300 rounded-lg p-3 font-mono text-sm break-all relative">
                {generated.plain_key}
              </div>
              <Button
                onClick={() => copyToClipboard(generated.plain_key)}
                className="w-full bg-[#52a675] hover:bg-[#3d8a5c]"
                data-testid="btn-copy-key"
              >
                {copied ? <><Check className="w-4 h-4 mr-2" /> Copié !</> : <><Copy className="w-4 h-4 mr-2" /> Copier la clé</>}
              </Button>
              <p className="text-xs text-gray-500">
                Nom : <strong>{generated.item?.name}</strong> · Préfixe public : <code className="text-[11px]">{generated.item?.key_prefix}</code>
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setGenerated(null)} variant="ghost">J'ai copié la clé, fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm revoke */}
      <Dialog open={!!confirmRevoke} onOpenChange={(o) => !o && setConfirmRevoke(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Révoquer cette clé ?</DialogTitle>
            <DialogDescription>
              La clé <strong>{confirmRevoke?.name}</strong> ne pourra plus authentifier de requêtes immédiatement. Elle reste visible dans la liste à titre d'historique.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmRevoke(null)}>Annuler</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => handleRevoke(confirmRevoke.id)} data-testid="btn-confirm-revoke">
              <ShieldOff className="w-4 h-4 mr-1" /> Révoquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer définitivement ?</DialogTitle>
            <DialogDescription>
              La clé <strong>{confirmDelete?.name}</strong> sera effacée de la base. Cette action est irréversible. Préfère la révocation si tu veux garder une trace.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Annuler</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(confirmDelete.id)} data-testid="btn-confirm-delete">
              <Trash2 className="w-4 h-4 mr-1" /> Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApiKeysPage;
